import { Extension, mergeAttributes, Node } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";

// Tabela z opcjonalnym tytułem (pasek nad tabelą) i legendą (przypis pod tabelą):
// <figure data-type="table-figure"><figcaption data-table-title>…</figcaption><table>…</table><div data-table-note>…</div></figure>
// Zwykła tabela bez tytułu i legendy zostaje gołym <table> (stare artykuły bez zmian).

const TableFigure = Node.create({
	name: "tableFigure",
	group: "block",
	content: "tableTitle? table tableNote?",
	isolating: true,

	parseHTML() {
		return [{ tag: 'figure[data-type="table-figure"]' }];
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"figure",
			mergeAttributes(HTMLAttributes, {
				"data-type": "table-figure",
				class: "tiptap-table-figure",
			}),
			0,
		];
	},
});

const TableTitle = Node.create({
	name: "tableTitle",
	content: "inline*",
	defining: true,

	parseHTML() {
		// tylko z atrybutem: zwykłe <figcaption> (podpisy zdjęć) nie mogą stać się tytułem tabeli
		return [{ tag: "figcaption[data-table-title]" }];
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"figcaption",
			mergeAttributes(HTMLAttributes, {
				"data-table-title": "",
				class: "tiptap-table-figure__title",
			}),
			0,
		];
	},
});

const TableNote = Node.create({
	name: "tableNote",
	content: "inline*",
	defining: true,

	parseHTML() {
		return [{ tag: "div[data-table-note]" }];
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-table-note": "",
				class: "tiptap-table-figure__note",
			}),
			0,
		];
	},
});

function findTable(state) {
	const { $from } = state.selection;
	for (let depth = $from.depth; depth > 0; depth--) {
		const node = $from.node(depth);
		if (node.type.name === "table") {
			const parent = $from.node(depth - 1);
			return {
				node,
				pos: $from.before(depth),
				figure:
					parent.type.name === "tableFigure"
						? { node: parent, pos: $from.before(depth - 1) }
						: null,
			};
		}
	}
	return null;
}

/** Czy tabela z kursorem ma tytuł / legendę — do etykiet w menu tabeli. */
export function getTableFigureParts(state) {
	const figure = findTable(state)?.figure?.node;
	let hasTitle = false;
	let hasNote = false;
	figure?.forEach((child) => {
		if (child.type.name === "tableTitle") hasTitle = true;
		if (child.type.name === "tableNote") hasNote = true;
	});
	return { hasTitle, hasNote };
}

const TableFigureCommands = Extension.create({
	name: "tableFigureCommands",

	addCommands() {
		return {
			// part: "tableTitle" | "tableNote" — dodaje pusty element i ustawia w nim kursor albo go usuwa
			toggleTablePart:
				(part) =>
				({ state, tr, dispatch }) => {
					const table = findTable(state);
					if (!table) return false;
					if (!dispatch) return true;

					const { schema } = state;

					if (!table.figure) {
						const children = [
							...(part === "tableTitle"
								? [schema.nodes.tableTitle.create()]
								: []),
							table.node,
							...(part === "tableNote"
								? [schema.nodes.tableNote.create()]
								: []),
						];
						tr.replaceWith(
							table.pos,
							table.pos + table.node.nodeSize,
							schema.nodes.tableFigure.create(null, children),
						);
						const partPos =
							part === "tableTitle"
								? table.pos + 2
								: table.pos + 1 + table.node.nodeSize + 1;
						tr.setSelection(TextSelection.create(tr.doc, partPos));
						dispatch(tr.scrollIntoView());
						return true;
					}

					const { node: figure, pos: figurePos } = table.figure;
					let existing = null;
					figure.forEach((child, offset) => {
						if (child.type.name === part) {
							existing = { node: child, pos: figurePos + 1 + offset };
						}
					});

					if (existing) {
						tr.delete(existing.pos, existing.pos + existing.node.nodeSize);
						// została sama tabela → zdejmujemy <figure>, żeby HTML był gołym <table>
						const rest = tr.doc.nodeAt(figurePos);
						if (rest?.childCount === 1) {
							tr.replaceWith(
								figurePos,
								figurePos + rest.nodeSize,
								rest.firstChild,
							);
						}
					} else {
						const insertPos =
							part === "tableTitle"
								? figurePos + 1
								: figurePos + figure.nodeSize - 1;
						tr.insert(insertPos, schema.nodes[part].create());
						tr.setSelection(TextSelection.create(tr.doc, insertPos + 1));
					}
					dispatch(tr.scrollIntoView());
					return true;
				},
		};
	},
});

export const TableFigureExtensions = [
	TableFigure,
	TableTitle,
	TableNote,
	TableFigureCommands,
];
